import { scheduleCartDeliveryEtaRefresh, scheduleCartDeliveryEtaSummaryRefresh } from '@theme/delivery-location-eta';
import { getCurrentMarket } from '@theme/delivery-location-context';
import { GCC_MARKET_CODES } from '@theme/market-context';

function formatMinorPrice(minor, currency) {
  const activeCurrency = currency || window.theme?.currency || window.theme?.defaultCurrency || 'AED';
  if (window.formatMinorPrice) return window.formatMinorPrice(minor, activeCurrency);
  return (minor / 100).toFixed(2);
}

let lastCartRewardsState = null;
let lastCartSummarySync = null;

function compareMinor(minor, fromCurrency, toCurrency) {
  if (minor == null || !fromCurrency || !toCurrency || fromCurrency === toCurrency) return minor;
  if (typeof window.convertMinorPrice === 'function') {
    return window.convertMinorPrice(minor, fromCurrency, toCurrency);
  }
  return minor;
}

function calculateVatMinor(taxableMinor, ratePercent) {
  const rate = parseInt(String(ratePercent || '0'), 10);
  if (!rate || rate <= 0 || taxableMinor <= 0) return 0;
  return Math.round((taxableMinor * rate) / (100 + rate));
}

function updateVatDisplay(vatEl, taxableMinor, currency) {
  if (!vatEl) return;
  const ratePercent = resolveVatRatePercent();
  vatEl.dataset.vatRatePercent = String(ratePercent);
  const vatMinor = calculateVatMinor(taxableMinor, ratePercent);
  vatEl.dataset.vatMinor = String(vatMinor);
  vatEl.removeAttribute('data-price-minor');
  vatEl.classList.toggle('cart__total-value--muted', ratePercent <= 0);
  const valueEl = vatEl.querySelector('[data-price-value]');
  const currencyEl = vatEl.querySelector('[data-price-currency]');
  if (valueEl) valueEl.textContent = String(ratePercent) + '%';
  if (currencyEl) {
    currencyEl.hidden = true;
    currencyEl.textContent = '';
  }
}

function isGccCountry(code) {
  return GCC_MARKET_CODES.includes(String(code || '').trim().toUpperCase());
}

function getCartRewardsProgressElements() {
  return Array.from(document.querySelectorAll('[data-cart-rewards-progress]'));
}

function resolveRewardsCountry() {
  try {
    const market = getCurrentMarket();
    if (market && market.code) return String(market.code).trim().toUpperCase();
  } catch (_) {}

  const progressElements = getCartRewardsProgressElements();
  const progress = progressElements[0];
  const sidebar = document.getElementById('cart-summary');
  const candidates = [
    progress && progress.dataset.marketCountry,
    sidebar && sidebar.dataset.marketCountry,
    window.theme && window.theme.marketBootstrap && window.theme.marketBootstrap.countryCode,
    window.theme && window.theme.country
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const code = String(candidates[i] || '').trim().toUpperCase();
    if (code) return code;
  }
  return '';
}

function resolveVatRatePercent() {
  const sidebar = document.getElementById('cart-summary');
  const serverRate = parseInt((sidebar && sidebar.dataset.vatRatePercent) || '', 10);
  if (!Number.isNaN(serverRate)) return serverRate;
  return isGccCountry(resolveRewardsCountry()) ? 5 : 0;
}

function syncDeliveryRewardMode(progress) {
  if (!progress) return false;
  const enabled = isGccCountry(resolveRewardsCountry());
  progress.dataset.deliveryRewardEnabled = enabled ? 'true' : 'false';
  progress.classList.toggle('cart-rewards-progress--discount-only', !enabled);
  const deliveryTerms = progress.querySelector('[data-rewards-delivery-terms]');
  if (deliveryTerms) deliveryTerms.hidden = !enabled;
  return enabled;
}

function isDeliveryRewardEnabled(progress) {
  if (!progress) return false;
  return syncDeliveryRewardMode(progress);
}

function setRewardsBarFill(bar, percent) {
  if (!bar) return;
  const fill = Math.min(100, Math.max(0, percent)) / 100;
  const fillPercent = String(fill * 100) + '%';
  bar.style.removeProperty('width');
  bar.style.removeProperty('left');
  bar.style.removeProperty('right');
  bar.style.setProperty('--rewards-fill', String(fill));
  bar.style.setProperty('--rewards-fill-percent', fillPercent);
}

function computeRewardsState(deliveryEnabled, subtotalMinor, discountEligibleMinor, deliveryThresholdMinor, discountThresholdMinor) {
  const deliveryReached = !deliveryEnabled || subtotalMinor >= deliveryThresholdMinor;
  const discountReached = discountEligibleMinor >= discountThresholdMinor;

  if (discountReached && deliveryReached) return 'complete';
  if (deliveryEnabled && !deliveryReached) return 'delivery';
  return 'discount';
}

function updateSingleCartRewardsProgress(progress, subtotalMinor, currency, discountEligibleMinor, state, shared) {
  const deliveryEnabled = isDeliveryRewardEnabled(progress);
  const {
    deliveryThresholdMinor,
    discountThresholdMinor,
    discountPercent,
    deliveryReached,
    discountReached,
    deliveryRemainingMinor,
    discountRemainingMinor,
    deliveryPercent,
    discountPercentComplete,
    previousState
  } = shared;

  progress.hidden = false;
  progress.dataset.rewardsState = state;
  progress.classList.toggle('cart-rewards-progress--complete', state === 'complete');
  progress.dataset.discountEligibleSubtotalMinor = String(discountEligibleMinor);
  progress.setAttribute('aria-valuenow', String(Math.min(discountEligibleMinor, discountThresholdMinor)));

  const setHidden = (selector, hidden) => {
    const element = progress.querySelector(selector);
    if (element) element.hidden = hidden;
  };
  const setAmount = (selector, minor) => {
    const amountWrap = progress.querySelector(selector);
    if (!amountWrap) return;
    amountWrap.dataset.priceMinor = String(minor);
    amountWrap.dataset.sourceCurrency = currency;
    delete amountWrap.dataset.currency;
  };

  setHidden('[data-rewards-delivery-text]', !deliveryEnabled || state !== 'delivery');
  setHidden('[data-rewards-discount-text]', state !== 'discount');
  setHidden('[data-rewards-complete-text]', state !== 'complete');
  setHidden(
    '[data-rewards-delivery-status]',
    progress.classList.contains('cart-rewards-progress--compact') ||
      !deliveryEnabled ||
      state !== 'discount'
  );

  setAmount('[data-rewards-delivery-amount]', deliveryRemainingMinor);
  setAmount('[data-rewards-discount-amount]', discountRemainingMinor);

  const rewardsBar = progress.querySelector('.cart-rewards-progress__bar');
  const rewardsMilestones = progress.querySelector('.cart-rewards-progress__milestones');
  const deliverySegment = progress.querySelector('.cart-rewards-progress__segment--delivery');
  if (rewardsBar) rewardsBar.hidden = false;
  if (rewardsMilestones) rewardsMilestones.hidden = !deliveryEnabled;
  if (deliverySegment) deliverySegment.hidden = !deliveryEnabled;

  const deliveryBar = progress.querySelector('[data-rewards-delivery-bar]');
  const discountBar = progress.querySelector('[data-rewards-discount-bar]');
  if (deliveryBar) setRewardsBarFill(deliveryBar, deliveryPercent);
  if (discountBar) setRewardsBarFill(discountBar, discountPercentComplete);

  document.dispatchEvent(new CustomEvent('theme:convert-prices'));

  const deliveryMilestone = progress.querySelector('[data-rewards-delivery-milestone]');
  const discountMilestone = progress.querySelector('[data-rewards-discount-milestone]');
  if (deliveryMilestone) deliveryMilestone.classList.toggle('is-active', deliveryReached);
  if (discountMilestone) discountMilestone.classList.toggle('is-active', discountReached);

  if (
    deliveryEnabled &&
    !progress.classList.contains('cart-rewards-progress--compact') &&
    lastCartRewardsState &&
    previousState !== state
  ) {
    const unlocked = state === 'complete' ? 'discount' : 'delivery';
    progress.dataset.rewardsUnlocked = unlocked;
    progress.classList.remove('cart-rewards-progress--celebrating');
    void progress.offsetWidth;
    progress.classList.add('cart-rewards-progress--celebrating');
    progress.dispatchEvent(new CustomEvent('cart:reward-unlocked', {
      bubbles: true,
      detail: { reward: unlocked, discountPercent: discountPercent }
    }));
    progress.addEventListener('animationend', function onRewardUnlockEnd(e) {
      if (e.animationName !== 'cart-reward-unlock') return;
      progress.removeEventListener('animationend', onRewardUnlockEnd);
      progress.classList.remove('cart-rewards-progress--celebrating');
      delete progress.dataset.rewardsUnlocked;
    });
  }
}

function updateCartRewardsProgress(subtotalMinor, currency, discountEligibleMinor) {
  const progressElements = getCartRewardsProgressElements();
  if (!progressElements.length) return;

  const progress = progressElements[0];

  const deliveryEnabled = isDeliveryRewardEnabled(progress);
  const thresholdCurrency = progress.dataset.deliveryThresholdCurrency || currency;
  const rawDeliveryThresholdMinor = parseInt(progress.dataset.deliveryThresholdMinor || '50000', 10);
  const deliveryThresholdMinor = deliveryEnabled
    ? compareMinor(rawDeliveryThresholdMinor, thresholdCurrency, currency)
    : 0;
  const discountThresholdMinor = parseInt(progress.dataset.discountThresholdMinor || '200000', 10);
  const discountPercent = progress.dataset.discountPercent || '5';
  const deliveryReached = !deliveryEnabled || subtotalMinor >= deliveryThresholdMinor;
  const discountReached = discountEligibleMinor >= discountThresholdMinor;
  const state = computeRewardsState(
    deliveryEnabled,
    subtotalMinor,
    discountEligibleMinor,
    deliveryThresholdMinor,
    discountThresholdMinor
  );
  const previousState = lastCartRewardsState != null ? lastCartRewardsState : progress.dataset.rewardsState;
  const deliveryRemainingMinor = deliveryEnabled ? Math.max(deliveryThresholdMinor - subtotalMinor, 0) : 0;
  const discountRemainingMinor = Math.max(discountThresholdMinor - discountEligibleMinor, 0);
  const deliveryPercent = deliveryEnabled
    ? Math.min(100, Math.max(0, (subtotalMinor / deliveryThresholdMinor) * 100))
    : 0;
  const discountRangeMinor = deliveryEnabled
    ? discountThresholdMinor - deliveryThresholdMinor
    : discountThresholdMinor;
  let discountPercentComplete = 0;
  if (deliveryEnabled) {
    discountPercentComplete = deliveryReached
      ? Math.min(100, Math.max(0, ((discountEligibleMinor - deliveryThresholdMinor) / discountRangeMinor) * 100))
      : 0;
  } else {
    discountPercentComplete = Math.min(100, Math.max(0, (discountEligibleMinor / discountThresholdMinor) * 100));
  }

  const shared = {
    deliveryThresholdMinor,
    discountThresholdMinor,
    discountPercent,
    deliveryReached,
    discountReached,
    deliveryRemainingMinor,
    discountRemainingMinor,
    deliveryPercent,
    discountPercentComplete,
    previousState
  };

  progressElements.forEach((element) => {
    updateSingleCartRewardsProgress(element, subtotalMinor, currency, discountEligibleMinor, state, shared);
  });
  document.dispatchEvent(new CustomEvent('theme:convert-prices'));
  lastCartRewardsState = state;
}

document.addEventListener('theme:delivery-rewards-thresholds', function(event) {
  const progressElements = getCartRewardsProgressElements();
  const progress = progressElements[0];
  if (progress && !isDeliveryRewardEnabled(progress)) {
    if (typeof lastCartSummarySync === 'function') {
      lastCartSummarySync();
      return;
    }
    return;
  }
  if (progress && event.detail && event.detail.deliveryThresholdMinor) {
    progressElements.forEach((element) => {
      element.dataset.deliveryThresholdMinor = String(event.detail.deliveryThresholdMinor);
      if (event.detail.deliveryThresholdCurrency) {
        element.dataset.deliveryThresholdCurrency = String(event.detail.deliveryThresholdCurrency);
      }
    });
    lastCartRewardsState = null;
  }
  if (typeof lastCartSummarySync === 'function') {
    lastCartSummarySync();
    return;
  }
  const fallbackProgressElements = getCartRewardsProgressElements();
  const fallbackProgress = fallbackProgressElements[0];
  if (!fallbackProgress) return;
  const sidebar = document.getElementById('cart-summary');
  const currency = (sidebar && sidebar.dataset.currency) || window.theme?.defaultCurrency || 'AED';
  const subtotalMinor = parseInt((sidebar && sidebar.dataset.cartSubtotalMinor) || '0', 10) || 0;
  const discountEligibleMinor = parseInt((sidebar && sidebar.dataset.cartDiscountEligibleSubtotalMinor) || '0', 10) || 0;
  updateCartRewardsProgress(subtotalMinor, currency, discountEligibleMinor);
});

document.addEventListener('theme:cart-delivery-price-updated', function() {
  if (typeof lastCartSummarySync === 'function') lastCartSummarySync();
});

document.addEventListener('theme:delivery-location-updated', function() {
  lastCartRewardsState = null;
  if (typeof lastCartSummarySync === 'function') lastCartSummarySync();
});

function updatePriceElement(el, minor, currency, serverTxt, allSelected) {
  if (!el) return;
  el.dataset.priceMinor = String(minor);
  el.dataset.originalPrice = String(minor);
  el.dataset.originalCurrency = currency;
  el.dataset.sourceCurrency = currency;
  const valueEl = el.querySelector('[data-price-value]');
  if (valueEl && allSelected && serverTxt) {
    valueEl.textContent = serverTxt;
  }
}

function readDeliveryMinor(deliveryValueEl) {
  if (!deliveryValueEl) return { deliveryMinor: 0, rateLoaded: false };
  if (deliveryValueEl.dataset.rateLoaded !== 'true') {
    return { deliveryMinor: 0, rateLoaded: false };
  }
  if (deliveryValueEl.classList.contains('cart__total-value--free')) {
    return { deliveryMinor: 0, rateLoaded: true };
  }
  if (deliveryValueEl.hasAttribute('data-price-minor')) {
    const deliveryMinorRaw = parseInt(deliveryValueEl.dataset.priceMinor || '0', 10);
    return { deliveryMinor: Number.isNaN(deliveryMinorRaw) ? 0 : deliveryMinorRaw, rateLoaded: true };
  }
  return { deliveryMinor: 0, rateLoaded: true };
}

function pulseCheckboxBox(checkbox) {
  if (!(checkbox instanceof HTMLInputElement)) return;
  const box = checkbox.nextElementSibling;
  if (!(box instanceof HTMLElement) || !box.classList.contains('cart__checkbox-box')) return;
  box.classList.remove('cart__checkbox-box--pulse');
  void box.offsetWidth;
  box.classList.add('cart__checkbox-box--pulse');
  box.addEventListener(
    'animationend',
    function() {
      box.classList.remove('cart__checkbox-box--pulse');
    },
    { once: true }
  );
}

function readLineQty(card) {
  const input = card.querySelector('input[name="updates[]"]');
  if (input instanceof HTMLInputElement && input.value) {
    const qty = parseInt(input.value, 10);
    if (!Number.isNaN(qty) && qty > 0) return qty;
  }
  const deliveryEl = card.querySelector('[data-delivery-line-qty]');
  if (deliveryEl) {
    const qty = parseInt(deliveryEl.getAttribute('data-delivery-line-qty') || '0', 10);
    if (!Number.isNaN(qty) && qty > 0) return qty;
  }
  return 1;
}

function readLineUnitPriceMinor(card) {
  const lineTotal = parseInt(card.dataset.lineTotalMinor || '0', 10);
  const qty = readLineQty(card);
  if (!Number.isNaN(lineTotal) && lineTotal > 0 && qty > 0) {
    const storedQty = parseInt(
      (card.querySelector('[data-delivery-line-qty]') && card.querySelector('[data-delivery-line-qty]').getAttribute('data-delivery-line-qty')) || '0',
      10
    );
    if (!Number.isNaN(storedQty) && storedQty > 0 && storedQty !== qty) {
      return Math.round(lineTotal / storedQty);
    }
    if (storedQty === qty) return Math.round(lineTotal / qty);
  }
  const priceEl = card.querySelector('.cart__card-price[data-price-minor]');
  if (!priceEl) return 0;
  const priceMinor = parseInt(priceEl.dataset.priceMinor || priceEl.getAttribute('data-price-minor') || '0', 10);
  if (Number.isNaN(priceMinor) || priceMinor <= 0) return 0;
  const cardQty = readLineQty(card);
  const storedQty = parseInt(
    (card.querySelector('[data-delivery-line-qty]') && card.querySelector('[data-delivery-line-qty]').getAttribute('data-delivery-line-qty')) || String(cardQty),
    10
  );
  if (!Number.isNaN(storedQty) && storedQty > 0 && storedQty !== cardQty) {
    return Math.round(priceMinor / storedQty);
  }
  return cardQty === 1 ? priceMinor : Math.round(priceMinor / cardQty);
}

function readLineTotalMinor(card) {
  const qty = readLineQty(card);
  const unitMinor = readLineUnitPriceMinor(card);
  if (unitMinor > 0 && qty > 0) return unitMinor * qty;
  const fromCard = parseInt(card.dataset.lineTotalMinor, 10);
  if (!Number.isNaN(fromCard) && fromCard > 0) return fromCard;
  const priceEl = card.querySelector('.cart__card-price[data-price-minor]');
  const fromPrice = priceEl ? parseInt(priceEl.dataset.priceMinor, 10) : NaN;
  return Number.isNaN(fromPrice) ? 0 : fromPrice;
}

function readSelectedItemQty(selected) {
  return selected.reduce(function(sum, item) {
    return sum + readLineQty(item.card);
  }, 0);
}

function syncSidebarDatasetFromHtml(html) {
  if (!html) return;
  const sidebar = document.getElementById('cart-summary');
  if (!sidebar) return;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nextSidebar = doc.getElementById('cart-summary');
  if (!nextSidebar) return;

  [
    'cartSubtotalMinor',
    'cartDiscountEligibleSubtotalMinor',
    'cartDiscountMinor',
    'cartTotalMinor',
    'currency',
    'displaySubtotal',
    'displayDiscount',
    'displayTotal'
  ].forEach(function(key) {
    if (nextSidebar.dataset[key] != null) {
      sidebar.dataset[key] = nextSidebar.dataset[key];
    }
  });
}

function syncSidebarFromCartUpdateEvent(event) {
  const cartItemsComponent = document.querySelector('cart-items-component[data-section-id]');
  const sectionId = cartItemsComponent && cartItemsComponent.dataset.sectionId;
  if (!sectionId || !event || !event.detail) return;

  const sections =
    event.detail.data && event.detail.data.sections
      ? event.detail.data.sections
      : event.detail.sections;

  if (sections && sections[sectionId]) {
    syncSidebarDatasetFromHtml(sections[sectionId]);
  }
}

function createCartSelectAllController(form) {
  const selectAll = form.querySelector('.cart__select-all-checkbox');
  if (!selectAll) return null;

  const getCards = () => form.querySelectorAll('.cart__card-item');

  const getCardCheckboxes = (card) => Array.from(card.querySelectorAll('.cart__card-checkbox'));

  const isCardSelected = (card) => {
    const boxes = getCardCheckboxes(card);
    if (!boxes.length) return true;
    return boxes[0].checked;
  };

  function syncCardCheckboxes(card, checked, source) {
    getCardCheckboxes(card).forEach(function(cb) {
      if (cb !== source) cb.checked = checked;
    });
  }

  function updateSelectAllState() {
    const cards = getCards();
    const allSelected =
      cards.length > 0 && Array.from(cards).every(function(card) {
        return isCardSelected(card);
      });
    selectAll.checked = allSelected;
  }

  function updateSummary() {
    const sidebar = document.getElementById('cart-summary');
    if (!sidebar) return;

    const countEl = document.getElementById('cart-selected-count');
    const subtotalEl = document.getElementById('cart-subtotal-value');
    const discountEl = document.getElementById('cart-discount-value');
    const vatEl = document.getElementById('cart-vat-value');
    const totalEl = document.getElementById('cart-total-value');
    const cards = getCards();
    const items = Array.from(cards).map(function(card) {
      return { card: card };
    });
    const selected = items.filter(function(x) {
      return isCardSelected(x.card);
    });
    const linesSubtotalMinor = selected.reduce((sum, x) => {
      return sum + readLineTotalMinor(x.card);
    }, 0);
    const linesDiscountEligibleMinor = selected.reduce((sum, x) => {
      if (x.card.dataset.discountEligible !== 'true') return sum;
      return sum + readLineTotalMinor(x.card);
    }, 0);
    const fullSubtotal = parseInt(sidebar.dataset.cartSubtotalMinor, 10) || 0;
    const fullDiscountEligible = parseInt(sidebar.dataset.cartDiscountEligibleSubtotalMinor, 10) || 0;
    const fullDiscount = parseInt(sidebar.dataset.cartDiscountMinor, 10) || 0;
    const fullTotal = parseInt(sidebar.dataset.cartTotalMinor, 10) || 0;
    const currency = sidebar.dataset.currency || window.theme?.defaultCurrency || 'AED';
    const allSelected = items.length > 0 && selected.length === items.length;
    const selectedSubtotalMinor = allSelected ? fullSubtotal : linesSubtotalMinor;
    const selectedDiscountEligibleMinor = allSelected ? fullDiscountEligible : linesDiscountEligibleMinor;
    const proratedDiscount =
      fullDiscount <= 0
        ? 0
        : allSelected
          ? fullDiscount
          : fullSubtotal > 0
            ? Math.round((fullDiscount * selectedSubtotalMinor) / fullSubtotal)
            : 0;
    const deliveryValueEl = document.getElementById('cart-delivery-value');
    const { deliveryMinor } = readDeliveryMinor(deliveryValueEl);
    const totalMinor = (allSelected ? fullTotal : selectedSubtotalMinor - proratedDiscount) + deliveryMinor;
    const taxableMinor = Math.max(0, (allSelected ? fullSubtotal : selectedSubtotalMinor) - proratedDiscount + deliveryMinor);

    updateCartRewardsProgress(selectedSubtotalMinor, currency, selectedDiscountEligibleMinor);

    if (countEl) countEl.textContent = selected.length;
    const subtotalCountEl = document.getElementById('cart-subtotal-count');
    if (subtotalCountEl) subtotalCountEl.textContent = readSelectedItemQty(selected);
    const suffixEl = document.getElementById('cart-selected-suffix');
    const selectedQty = readSelectedItemQty(selected);
    if (suffixEl) suffixEl.textContent = selectedQty === 1 ? ' item' : ' items';

    updatePriceElement(subtotalEl, selectedSubtotalMinor, currency, sidebar.dataset.displaySubtotal, allSelected);
    if (discountEl && fullDiscount > 0) {
      updatePriceElement(discountEl, proratedDiscount, currency, sidebar.dataset.displayDiscount, allSelected);
    }
    updateVatDisplay(vatEl, taxableMinor, currency);
    if (totalEl) {
      totalEl.setAttribute('value', (totalMinor / 100).toFixed(2));
      updatePriceElement(totalEl, totalMinor, currency, sidebar.dataset.displayTotal, allSelected);
    }

    const mobileCount = document.getElementById('cart-mobile-bar-count');
    const mobileTotal = document.querySelector('[data-cart-mobile-bar] .cart-mobile-bar__total');
    if (mobileCount) mobileCount.textContent = String(selected.length);
    updatePriceElement(mobileTotal, totalMinor, currency, sidebar.dataset.displayTotal, allSelected);
    document.dispatchEvent(new CustomEvent('theme:convert-prices'));
  }

  lastCartSummarySync = updateSummary;

  function onSelectAllChange() {
    getCards().forEach(function(card) {
      syncCardCheckboxes(card, selectAll.checked, null);
    });
    pulseCheckboxBox(selectAll);
    updateSummary();
    scheduleCartDeliveryEtaSummaryRefresh();
  }

  function onCardCheckboxChange(event) {
    const card = event.target.closest('.cart__card-item');
    if (!card) return;
    syncCardCheckboxes(card, event.target.checked, event.target);
    getCardCheckboxes(card).forEach(pulseCheckboxBox);
    updateSelectAllState();
    updateSummary();
    scheduleCartDeliveryEtaSummaryRefresh();
  }

  return {
    selectAll,
    getCards,
    getCardCheckboxes,
    onSelectAllChange,
    onCardCheckboxChange,
    updateSelectAllState,
    updateSummary
  };
}

export function initCartSelectAll() {
  const form = document.getElementById('cart-form');
  if (!form) return;

  const controller = createCartSelectAllController(form);
  if (!controller) return;

  const { selectAll, getCards, getCardCheckboxes, onSelectAllChange, onCardCheckboxChange, updateSelectAllState, updateSummary } = controller;

  selectAll.removeEventListener('change', onSelectAllChange);
  selectAll.addEventListener('change', onSelectAllChange);
  getCards().forEach(function(card) {
    getCardCheckboxes(card).forEach(function(cb) {
      cb.removeEventListener('change', onCardCheckboxChange);
      cb.addEventListener('change', onCardCheckboxChange);
    });
  });
  updateSelectAllState();
  updateSummary();
}

let cartSelectAllInitTimer = 0;

function resetCartDeliveryEtaPendingState() {
  document.querySelectorAll('[data-delivery-line-variant]').forEach(function(lineEl) {
    lineEl.setAttribute('data-delivery-eta-pending', 'true');
    lineEl.removeAttribute('data-delivery-eta-ready');
  });
  document.querySelectorAll('[data-delivery-estimate-scope="cart"], .cart__delivery').forEach(function(root) {
    root.setAttribute('data-delivery-eta-pending', 'true');
    root.removeAttribute('data-delivery-eta-ready');
  });
  const deliveryValue = document.getElementById('cart-delivery-value');
  if (deliveryValue) {
    deliveryValue.setAttribute('data-delivery-eta-pending', 'true');
    deliveryValue.removeAttribute('data-delivery-eta-ready');
    deliveryValue.dataset.rateLoaded = 'false';
  }
}

function scheduleInitCartSelectAll(options) {
  const refreshDelivery = !!(options && options.refreshDelivery);
  clearTimeout(cartSelectAllInitTimer);
  cartSelectAllInitTimer = setTimeout(function() {
    cartSelectAllInitTimer = 0;
    lastCartRewardsState = null;
    if (refreshDelivery) {
      resetCartDeliveryEtaPendingState();
      scheduleCartDeliveryEtaRefresh();
    }
    initCartSelectAll();
  }, 0);
}

function onCartQuantityChanged(event) {
  syncSidebarFromCartUpdateEvent(event);
  scheduleInitCartSelectAll({ refreshDelivery: true });
}

function bootCartSelectAll() {
  const bootInit = function() {
    scheduleInitCartSelectAll({ refreshDelivery: false });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootInit);
  } else {
    bootInit();
  }
  document.addEventListener('shopify:section:load', bootInit);
  document.addEventListener('cart:update', onCartQuantityChanged);
  document.addEventListener('cart:updated', onCartQuantityChanged);
  document.addEventListener('shopify:cart:update', onCartQuantityChanged);
}

bootCartSelectAll();
